<!--
  StudySlideResponsiveDataTableCard: desktop table + horizontal scroll; at max-width 640px
  the table is hidden and the same rows render as stacked cards (adc-oncology s1b / s3b).
-->
<script lang="ts">
import type { StudySlideResponsiveDataTableCardProps } from '$lib/components/study/slide-layout-types'

type Props = StudySlideResponsiveDataTableCardProps

let props: Props = $props()
</script>

<div class="study-slide-table">
  <div class="study-slide-table__wrap">
    <div
      class="study-slide-table__scroll"
      style="overflow-x:auto;border-radius:10px;border:1px solid var(--gbr);background:var(--table-surface, rgba(0,0,0,.12));"
    >
      {#if props.variant === 'generic'}
        <table class="study-slide-table__table">
          <thead>
            <tr style="background:rgba(17,160,165,.07);">
              {#each props.headers as header, hi}
                <th
                  style="text-align:{props.columnAlign?.[hi] ?? 'left'};padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--mu);font-weight:600;"
                >
                  {header}
                </th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each props.rows as row, i}
              <tr style:background={i % 2 === 1 ? 'var(--row-stripe)' : 'transparent'}>
                {#each row as cell, ci}
                  <td
                    style="padding:.36rem .55rem;text-align:{props.columnAlign?.[ci] ?? 'left'};{ci === 0 ? 'color:var(--mu);' : ''}"
                  >
                    {@html cell}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      {:else if props.variant === 'compare'}
        <table class="study-slide-table__table">
          <thead>
            <tr style="background:rgba(17,160,165,.07);">
              <th
                style="text-align:left;padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--mu);font-weight:600;width:18%;"
              >
                Dimension
              </th>
              <th
                style="text-align:left;padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--mu);font-family:'DM Serif Display',serif;font-size:.8rem;font-weight:400;"
              >
                Conventional chemotherapy
              </th>
              <th
                style="text-align:left;padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--g);font-family:'DM Serif Display',serif;font-size:.8rem;font-weight:400;"
              >
                ADC
              </th>
            </tr>
          </thead>
          <tbody>
            {#each props.rows as row, i}
              <tr style:background={i % 2 === 1 ? 'var(--row-stripe)' : 'transparent'}>
                <td style="padding:.36rem .55rem;color:var(--mu);vertical-align:top;">
                  {row.dimension}
                </td>
                <td style="padding:.36rem .55rem;">{row.conventional}</td>
                <td style="padding:.36rem .55rem;">{row.adc}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {:else}
        <table class="study-slide-table__table">
          <thead>
            <tr style="background:rgba(17,160,165,.07);">
              <th
                style="text-align:left;padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--mu);font-weight:600;width:28%;"
              >
                Theme
              </th>
              <th
                style="text-align:left;padding:.38rem .55rem;border-bottom:1px solid var(--gbr);color:var(--g);font-family:'DM Serif Display',serif;font-size:.8rem;font-weight:400;"
              >
                IMX fields
              </th>
            </tr>
          </thead>
          <tbody>
            {#each props.rows as row, i}
              <tr style:background={i % 2 === 1 ? 'var(--row-stripe)' : 'transparent'}>
                <td style="padding:.36rem .55rem;color:var(--mu);vertical-align:top;">
                  {row.theme}
                </td>
                <td
                  style="padding:.36rem .55rem;font-family:ui-monospace,monospace;font-size:.72rem;line-height:1.45;"
                >
                  {row.fields}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  </div>

  {#if props.variant === 'compare'}
    <div class="study-slide-table__cards">
      {#each props.rows as row, i}
        <div class="study-slide-table__card" class:study-slide-table__card--alt={i % 2 === 1}>
          <div class="study-slide-table__card-title">{row.dimension}</div>
          <div class="study-slide-table__card-section">
            <div class="study-slide-table__card-label study-slide-table__card-label--muted">
              Conventional chemotherapy
            </div>
            <p class="study-slide-table__card-text">{row.conventional}</p>
          </div>
          <div class="study-slide-table__card-section study-slide-table__card-section--adc">
            <div class="study-slide-table__card-label study-slide-table__card-label--accent">
              ADC
            </div>
            <p class="study-slide-table__card-text">{row.adc}</p>
          </div>
        </div>
      {/each}
    </div>
  {:else if props.variant === 'dictionary'}
    <div class="study-slide-table__cards study-slide-table__cards--compact">
      {#each props.rows as row, i}
        <div class="study-slide-table__card" class:study-slide-table__card--alt={i % 2 === 1}>
          <div class="study-slide-table__card-title study-slide-table__card-title--sm">
            {row.theme}
          </div>
          <p class="study-slide-table__card-fields">{row.fields}</p>
        </div>
      {/each}
    </div>
  {:else if props.variant === 'generic'}
    <div class="study-slide-table__cards study-slide-table__cards--compact">
      {#each props.rows as row, i}
        <div class="study-slide-table__card" class:study-slide-table__card--alt={i % 2 === 1}>
          {#each row as cell, ci}
            <div style="display:flex;gap:.45rem;margin-bottom:.3rem;{ci === row.length - 1 ? 'margin-bottom:0;' : ''}">
              <span style="font-size:.58rem;text-transform:uppercase;letter-spacing:.07em;font-weight:700;color:var(--mu);white-space:nowrap;padding-top:.12em;">{props.headers[ci] ?? '—'}</span>
              <span style="font-size:.76rem;line-height:1.45;">{@html cell}</span>
            </div>
          {/each}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
.study-slide-table__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  line-height: 1.42;
}
.study-slide-table__cards {
  display: none;
  flex-direction: column;
  gap: 0.65rem;
}
.study-slide-table__cards--compact {
  gap: 0.55rem;
}
.study-slide-table__card {
  border-radius: 10px;
  border: 1px solid var(--gbr, rgba(255, 255, 255, 0.12));
  background: var(--table-surface, rgba(0, 0, 0, 0.12));
  padding: 0.85rem 0.95rem 0.9rem;
  overflow: hidden;
}
.study-slide-table__card--alt {
  background: var(--card-alt-bg, rgba(0, 0, 0, 0.18));
}
.study-slide-table__card-title {
  font-family: 'DM Serif Display', serif;
  font-size: 0.95rem;
  color: var(--slide-fg, #fff);
  margin-bottom: 0.65rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--gbr, rgba(255, 255, 255, 0.12));
}
.study-slide-table__card-title--sm {
  font-size: 0.88rem;
  margin-bottom: 0.4rem;
  padding-bottom: 0.35rem;
}
.study-slide-table__card-section {
  margin-bottom: 0.75rem;
}
.study-slide-table__card-section:last-child {
  margin-bottom: 0;
}
.study-slide-table__card-section--adc {
  padding-top: 0.55rem;
  margin-top: 0.1rem;
  border-top: 1px dashed rgba(17, 160, 165, 0.35);
}
.study-slide-table__card-label {
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  margin-bottom: 0.28rem;
}
.study-slide-table__card-label--muted {
  color: var(--mu, rgba(255, 255, 255, 0.45));
}
.study-slide-table__card-label--accent {
  color: var(--g, #11a0a5);
}
.study-slide-table__card-text {
  margin: 0;
  font-size: 0.76rem;
  line-height: 1.52;
  color: var(--t2, rgba(255, 255, 255, 0.78));
}
.study-slide-table__card-fields {
  margin: 0;
  font-family: ui-monospace, monospace;
  font-size: 0.7rem;
  line-height: 1.48;
  color: var(--t2, rgba(255, 255, 255, 0.78));
}
@media (max-width: 640px) {
  .study-slide-table__wrap {
    display: none;
  }
  .study-slide-table__cards {
    display: flex;
  }
}
</style>
